import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  FileText, 
  Briefcase, 
  Puzzle, 
  MapPin, 
  LogOut,
  Search
} from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { useAdmin } from "@/hooks/useAdmin";

const Admin = () => {
  const navigate = useNavigate();
  const { isAdmin, loading } = useAdmin();

  useSEO({
    title: "Admin Dashboard | Ommni",
    description: "Admin dashboard for managing Ommni content",
    noindex: true,
  });

  useEffect(() => {
    if (!loading && !isAdmin) {
      navigate("/auth");
    }
  }, [isAdmin, loading, navigate]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  const adminSections = [
    {
      title: "Blog Posts",
      description: "Manage blog posts, categories, and content",
      icon: FileText,
      path: "/admin/blog",
      color: "text-blue-500",
    },
    {
      title: "Job Postings",
      description: "Create and manage job listings",
      icon: Briefcase,
      path: "/admin/jobs",
      color: "text-green-500",
    },
    {
      title: "Integrations",
      description: "Manage integration marketplace",
      icon: Puzzle,
      path: "/admin/integrations",
      color: "text-purple-500",
    },
    {
      title: "Roadmap",
      description: "Update product roadmap and features",
      icon: MapPin,
      path: "/admin/roadmap",
      color: "text-orange-500",
    },
    {
      title: "SEO Health Check",
      description: "Monitor and optimize site SEO",
      icon: Search,
      path: "/admin/seo-health-check",
      color: "text-pink-500",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Admin Dashboard</h1>
            <p className="text-muted-foreground">
              Manage your Ommni content and settings
            </p>
          </div>
          <Button variant="outline" onClick={handleSignOut}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign Out
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {adminSections.map((section) => (
            <Link key={section.path} to={section.path}>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
                <CardHeader>
                  <div className={`${section.color} mb-4`}>
                    <section.icon className="h-8 w-8" />
                  </div>
                  <CardTitle>{section.title}</CardTitle>
                  <CardDescription>{section.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="ghost" className="w-full">
                    Open {section.title}
                  </Button>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Admin;
